import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { fetchAllTokenHolders } from "@/lib/friendtech";
import { parseFile } from "@/lib/parse";
import { cn } from "@/lib/utils";
import { CID } from "ipfs-http-client";
import { useState } from "react";
import Dropzone from "react-dropzone";
import { useForm } from "react-hook-form";
import { Address, useMutation, useQuery } from "wagmi";
import { uploadFile } from "../../api/ipfs";
import { Button } from "../../components/Button";
import { FormItem } from "../../components/FormItem";
import { formatEntriesList } from "../../utils/formatters";
import { Fields, createMetadata } from "../../utils/useMetadata";
import { getAddress } from "ethers/lib/utils.js";

const radioBoxClassNames = [
  "flex",
  "items-center",
  "rounded",
  "border",
  "border-gray-200",
  "pl-4",
  "dark:border-gray-700",
];

export function RaffleForm({ onSuccess }: { onSuccess?: (cid: CID) => void }) {
  const form = useForm<Fields>({
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      winners: undefined,
      entries: "",
      raffleType: "friendtech",
      minBlocksToWait: 4,
    },
  });

  const [friendTechWalletAddress, setFriendTechWalletAddress] =
    useState<string>("");

  const { refetch: fetchFriendtechTokenHolders, isLoading } = useQuery(
    ["tokenHolders", friendTechWalletAddress],
    async () => {
      try {
        getAddress(friendTechWalletAddress);
      } catch (e) {
        alert("Invalid address");
        return null;
      }

      return fetchAllTokenHolders(friendTechWalletAddress, {
        shouldIncludeSelf: (
          document.getElementById("includeOwner") as HTMLInputElement
        )?.checked,
      });
    },
    {
      enabled: false,
      onSuccess(data) {
        if (!data || "error" in data) {
          form.setValue("entries", "");
          return;
        }

        const isWeightedRaffle = (
          document.getElementById("weighted") as HTMLInputElement
        )?.checked;

        const addressList = data.holders.reduce<Address[]>((_, holder) => {
          const newAddresses = Array.from<Address>({
            length: isWeightedRaffle ? holder.keyCount : 1,
          }).fill(holder.address as Address);

          return _.concat(...newAddresses);
        }, []);

        form.setValue("entries", addressList.join("\n"));
      },
    }
  );

  const [uploadProgress, setUploadProgress] = useState(0);

  const entries = form.watch("entries");
  const raffleType = form.watch("raffleType");
  const entriesType = raffleType === "string" ? "string" : "address";

  const entriesListResult = formatEntriesList(entries, entriesType);

  const entriesCount = entriesListResult?.entries.length ?? 0;

  const { mutateAsync: upload } = useMutation(uploadFile);

  async function onSubmit(fields: Fields) {
    const metadata = createMetadata({ ...fields, entriesType });

    setUploadProgress(0);

    const targetSize = Buffer.from(metadata).byteLength;

    const { cid } = await upload({
      data: metadata,
      progress: (bytes) => setUploadProgress(bytes / targetSize),
    });

    onSuccess?.(cid);
  }

  async function handleDrop(files: File[]) {
    if (!files.length) return;
    if (
      entriesCount &&
      !confirm("This will replace all entries. Are you sure?")
    )
      return;

    let entries = "";

    for await (const file of files) {
      const content = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.addEventListener("loadend", (e) => {
          if (!reader.result) throw new Error();
          resolve(reader.result.toString());
        });

        reader.readAsText(file);
      });

      entries += `\n${parseFile(content).join(`\n`)}`;
    }

    form.setValue("entries", entries.trim());
  }

  return (
    <>
      <form id="raffle" onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset disabled={form.formState.isSubmitting} className="space-y-6">
          <FormItem
            label="Name of the raffle"
            errors={form.formState.errors as any}
            type="text"
            placeholder="The fairest raffle ever…"
            required
            {...form.register("name", {
              required: "This field is required.",
              minLength: {
                value: 3,
                message: "Name must be at least 3 characters long.",
              },
              maxLength: {
                value: 100,
                message: "Name must be 100 characters or less.",
              },
            })}
          />
          <FormItem
            label="Number of winners"
            errors={form.formState.errors as any}
            type="number"
            placeholder="420"
            step={1}
            min={1}
            required
            max={entriesCount}
            {...form.register("winners", {
              required: "This field is required.",
              valueAsNumber: true,
              min: {
                value: 1,
                message: "Minimum 1 winner.",
              },
              max: {
                value: entriesCount,
                message: "Cannot be larger than the number of entries.",
              },
            })}
          />
          <div className="space-y-2">
            <Label>What type of entries are you collecting?</Label>
            <label
              htmlFor="radio-entries-type-friendtech"
              className={
                raffleType === "friendtech"
                  ? radioBoxClassNames
                      .concat("bg-blue-100 dark:bg-blue-600")
                      .join(" ")
                  : radioBoxClassNames.join(" ")
              }
            >
              <input
                {...form.register("raffleType")}
                type="radio"
                value="friendtech"
                id="radio-entries-type-friendtech"
                className="h-4 w-4 border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
              />
              <div className="ml-2 w-full py-4 text-sm font-medium text-gray-900 dark:text-gray-300">
                <span className="font-medium text-gray-900 dark:text-gray-300">
                  friend.tech key holders
                </span>
                <p
                  id="helper-radio-text"
                  className="text-xs font-normal text-gray-500 dark:text-gray-300"
                >
                  This lets you search for friend.tech wallet addresses and
                  import all keyholders of that profile
                </p>
              </div>
            </label>
            <label
              htmlFor="radio-entries-type-address"
              className={
                raffleType === "address"
                  ? radioBoxClassNames
                      .concat("bg-blue-100 dark:bg-blue-600")
                      .join(" ")
                  : radioBoxClassNames.join(" ")
              }
            >
              <input
                {...form.register("raffleType")}
                type="radio"
                value="address"
                id="radio-entries-type-address"
                className="h-4 w-4 border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
              />
              <div className="ml-2 w-full py-4 text-sm font-medium text-gray-900 dark:text-gray-300">
                <span className="font-medium text-gray-900 dark:text-gray-300">
                  Ethereum-compatible wallet addresses
                </span>
                <p
                  id="helper-radio-text"
                  className="text-xs font-normal text-gray-500 dark:text-gray-300"
                >
                  Optimised for maximum onchain composability.
                </p>
              </div>
            </label>
            <label
              htmlFor="radio-entries-type-string"
              className={
                raffleType === "string"
                  ? radioBoxClassNames
                      .concat("bg-blue-100 dark:bg-blue-600")
                      .join(" ")
                  : radioBoxClassNames.join(" ")
              }
            >
              <input
                {...form.register("raffleType")}
                type="radio"
                value="string"
                id="radio-entries-type-string"
                className="h-4 w-4 border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
              />
              <div className="ml-2 w-full py-4 text-sm font-medium text-gray-900 dark:text-gray-300">
                <span className="font-medium text-gray-900 dark:text-gray-300">
                  Something else
                </span>
                <p
                  id="helper-radio-text"
                  className="text-xs font-normal text-gray-500 dark:text-gray-300"
                >
                  Choose this if you want to collect email addresses, Twitter
                  usernames, or other types of identifiers as raffle entries.
                </p>
              </div>
            </label>
          </div>
          {raffleType === "friendtech" && (
            <>
              <fieldset disabled={isLoading} className="flex">
                <Input
                  type="text"
                  placeholder="Input your friend.tech wallet address"
                  value={friendTechWalletAddress}
                  onChange={(event) => {
                    setFriendTechWalletAddress(event.target.value);
                  }}
                />
                <button
                  className="rounded bg-blue-500 py-2 px-4 text-sm text-white hover:bg-blue-700"
                  onClick={(event) => {
                    event.preventDefault();
                    fetchFriendtechTokenHolders();
                  }}
                >
                  {isLoading ? "Loading…" : "Import keyholders"}
                </button>
              </fieldset>
              <div className="space-y-4">
                <label htmlFor="weighted" className="flex items-center gap-2">
                  <div>
                    <Input
                      defaultChecked={true}
                      id="weighted"
                      type="checkbox"
                    />
                  </div>
                  <div>
                    <div className="">One entry per key (Weighted raffle)</div>
                    <div className="text-sm text-gray-400">
                      By default every holder gets multiple entries if they hold
                      more than one key. <br />
                      Uncheck this if every holder should only get one entry
                      regardless of their key count.
                    </div>
                  </div>
                </label>
                <label
                  htmlFor="includeOwner"
                  className="flex items-center gap-2"
                >
                  <div>
                    <Input
                      defaultChecked={false}
                      id="includeOwner"
                      type="checkbox"
                    />
                  </div>
                  <div>
                    <div>Include owner in address list</div>
                  </div>
                </label>
              </div>
            </>
          )}
          <Dropzone
            noClick
            noKeyboard
            accept={{ "text/*": [] }}
            onDrop={handleDrop}
          >
            {({ getRootProps, open, isDragAccept }) => (
              <div
                {...getRootProps()}
                className={cn(
                  `pt-1`,
                  isDragAccept && `rounded-md outline outline-2 outline-primary`
                )}
              >
                <FormItem
                  label={
                    <>
                      Entries{" "}
                      {!!entriesListResult && (
                        <span className="text-xs">
                          ({entriesCount} entries)
                        </span>
                      )}
                    </>
                  }
                  errors={form.formState.errors as any}
                  type="textarea"
                  placeholder={`
Input here the list of entries to be drawn in the raffle.
            `.trim()}
                  rows={10}
                  spellCheck={false}
                  className="font-mono"
                  required
                  {...form.register("entries", {
                    required: "This field is required.",
                    deps: ["winners"],
                  })}
                />
                <button
                  onClick={open}
                  className="-mt-4 block w-full rounded-md border-2 border-dashed px-4 pt-3.5 pb-1.5 text-left text-xs"
                >
                  Drop text or CSV files to populate the entries. One entry per
                  line. For CSVs, we only use the first column.
                </button>
              </div>
            )}
          </Dropzone>
          <FormItem
            type="number"
            label="Minimum number of blocks to wait for random seed (more = better!)"
            errors={form.formState.errors as any}
            step={1}
            min={1}
            required
            {...form.register("minBlocksToWait", {
              required: "This field is required.",
              valueAsNumber: true,
              min: {
                value: 1,
                message: "Must wait a minimum of 1 block.",
              },
            })}
          />

          {!!entriesListResult?.duplicates && (
            <div className="flex gap-4 space-y-2 rounded-lg bg-sky-100 px-4 py-2 dark:bg-sky-900">
              <div className="py-3 text-3xl">💡</div>
              <div>
                <p className="font-semibold">
                  Duplicate entries (weighted raffle) detected:
                </p>
                <ul>
                  {Object.entries(entriesListResult.duplicates).map(
                    ([entry, count]) => (
                      <li key={entry}>
                        <span className="tabular-nums">{count}x</span>{" "}
                        <span className="font-mono">{entry}</span>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          )}

          {entriesListResult && !entriesListResult.valid && (
            <div className="flex gap-4 space-y-2 rounded-lg bg-red-100 px-4 py-2 dark:bg-red-900">
              <div className="py-3 text-3xl">😣</div>
              <div>
                <p className="font-semibold">Invalid addresses detected:</p>
                <ul>
                  {entriesListResult.errors.map(({ entry, line }) => (
                    <li key={line}>
                      <span className="font-mono">
                        {`"`}
                        {entry}
                        {`"`} (line {line})
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </fieldset>
      </form>

      <div className="mt-12 space-y-2">
        {form.formState.isSubmitting ? (
          <>
            <Button form="raffle" progress={uploadProgress} disabled>
              Uploading to IPFS…
            </Button>
            <p className="text-xs text-gray-500">
              Please wait for the IPFS upload to complete.
            </p>
          </>
        ) : (
          <>
            <Button
              form="raffle"
              progress={uploadProgress}
              disabled={!entriesListResult || !entriesListResult.valid}
            >
              Upload metadata
            </Button>
          </>
        )}
      </div>
    </>
  );
}
